<div class="modal-header">
  <h4 class="modal-title">{{ isNew ? "Create Firmware Instruction" : "Edit Firmware Instruction" }}</h4>
  <button class="close" aria-label="Close" (click)="activeModal.dismiss()">
    <span aria-hidden="true">&times;</span>
  </button>
</div>
<div class="modal-body" ngbAutofocus>
  <div class="form-group">
    <label class="control-label">
      Title
      <span class="text-danger">*</span>
    </label>
    <input type="text" class="form-control" [(ngModel)]="title" placeholder="Admin display title" />
  </div>

  <div class="form-group m-t-10">
    <label class="control-label">
      Mode
      <span class="text-danger">*</span>
    </label>
    <select class="form-control" [(ngModel)]="mode">
      @for (m of modes; track m) {
        <option [value]="m">{{ m }}</option>
      }
    </select>
  </div>

  <div class="form-group m-t-10">
    <label class="control-label">
      mainContent (template id)
      <span class="text-danger">*</span>
    </label>
    <small class="text-muted d-block">e.g. firmware_instructions/update_firmware_aimixers</small>
    <input type="text" class="form-control" [(ngModel)]="mainContent" placeholder="firmware_instructions/..." />
  </div>

  <div class="m-t-20">
    <label class="control-label">Associated Products</label>
    <myp-product-search-picker
      hint="Search by product title and add product IDs."
      selectedHeader="Products on this document"
      searchPlaceholder="Search products..."
      [products]="associatedProducts"
      subDataMode="id"
      resultListClass="m-b-20"
      selectedListClass="m-b-20"
      addTooltip="Add Product"
      removeTooltip="Remove Product"
      (productSelected)="addProduct($event.product)"
      (productRemoved)="removeProduct($event)"
    ></myp-product-search-picker>
  </div>

  <div class="m-t-20">
    <div class="d-flex align-items-center justify-content-between m-b-10 flex-wrap" style="gap: 10px">
      <div class="d-flex align-items-center flex-wrap" style="gap: 10px">
        <label class="control-label m-b-0">
          Localized content
          <span class="text-danger">*</span>
        </label>
        @if (missingTranslations) {
          <span class="c-red pill red sm">
            <fa-icon icon="exclamation-triangle"></fa-icon>
            Missing translations
          </span>
        } @else if (otherLanguages.length) {
          <span class="pill green sm">
            <fa-icon icon="check"></fa-icon>
            Fully Translated
          </span>
        }
        <div class="firmware-content-mode-toggle" role="group" aria-label="Content view mode">
          <button type="button" [class.active]="contentViewMode === 'edit'" [disabled]="translating" (click)="setContentViewMode('edit')">
            <fa-icon icon="pencil" class="m-r-5"></fa-icon>
            Edit
          </button>
          <button type="button" [class.active]="contentViewMode === 'preview'" [disabled]="translating" (click)="setContentViewMode('preview')">
            <fa-icon icon="eye" class="m-r-5"></fa-icon>
            Preview
          </button>
        </div>
      </div>
      <div class="d-flex align-items-center">
        <button type="button" class="btn btn-sm btn-blank m-r-10" [disabled]="translating" (click)="showConfig = !showConfig">Configure Auto-translate</button>
        <button type="button" class="btn btn-sm btn-secondary" [disabled]="translating || saving" (click)="autoTranslate()">
          {{ translating ? "Translating…" : "Auto-translate" }}
        </button>
      </div>
    </div>

    @if (showConfig) {
      <div class="p-15 m-b-15" style="background: rgba(0, 0, 0, 0.03); border-radius: 6px">
        <label class="t-14 b m-b-5">Terms to never translate</label>
        <p class="t-12 text-muted m-b-5">Comma-separated. Sent to the translator and kept verbatim (brand and product names, etc.).</p>
        <textarea class="form-control t-14" rows="3" [(ngModel)]="doNotTranslateTerms" name="doNotTranslateTerms"></textarea>
        <div class="d-flex m-t-10">
          <button type="button" class="btn btn-sm btn-primary m-r-10" [disabled]="savingConfig" (click)="saveConfig()">
            {{ savingConfig ? "Saving…" : "Save config" }}
          </button>
          <button type="button" class="btn btn-sm btn-blank" [disabled]="savingConfig" (click)="showConfig = false">Close</button>
        </div>
      </div>
    }

    <p class="t-12 text-muted m-b-10">Author the English copy, then Auto-translate to fill the other languages. Use Preview to see how the HTML will render for customers.</p>

    @if (translateError) {
      <div class="alert alert-danger t-12">{{ translateError }}</div>
    }

    @if (contentViewMode === "preview") {
      <div class="firmware-preview-shell">
        <div class="firmware-preview-chrome">
          <div class="firmware-preview-header">
            <div>
              <div class="firmware-preview-kicker">Customer preview</div>
              <h4>Firmware Instructions</h4>
            </div>
            <span class="pill sm gray">{{ previewLangLabel }} ({{ previewLang }})</span>
          </div>

          <div class="firmware-preview-langs">
            @for (lang of previewLangOptions; track lang.code) {
              <button type="button" class="firmware-preview-lang-btn" [class.active]="previewLang === lang.code" [class.missing]="lang.missing" (click)="setPreviewLang(lang.code)">
                {{ lang.label }}
                @if (lang.missing) {
                  <span class="m-l-5">·</span>
                }
              </button>
            }
          </div>

          <div class="firmware-preview-body">
            @if (previewEmpty) {
              <div class="firmware-preview-empty">
                <fa-icon icon="file" class="preview-empty-icon"></fa-icon>
                <div>
                  <strong>No rendered content yet</strong>
                  <div class="t-12">Switch to Edit and add HTML for {{ previewLangLabel }}.</div>
                </div>
              </div>
            } @else {
              <div class="quill-content" [innerHTML]="previewHtml | safe: 'html'"></div>
            }
          </div>
        </div>
      </div>
    } @else {
      <div class="form-group">
        <label class="control-label">English (en) — source</label>
        @if (quillReady) {
          <fui-quill-editor [content]="translations.en" (contentChange)="onTranslationChange('en', $event)" placeholder="Write English instruction HTML..." height="280px"></fui-quill-editor>
        }
      </div>

      @if (translating) {
        <div class="p-15 m-t-15 secondary-content-bg border-radius10">
          <div class="d-flex align-items-center m-b-10">
            <fa-icon icon="spinner" animation="spin" class="m-r-10"></fa-icon>
            <strong>Translating other languages…</strong>
          </div>
          <p class="t-12 text-muted m-b-10">Other languages stay collapsed until translation finishes.</p>
          <div class="d-flex flex-wrap">
            @for (lang of otherLanguages; track lang.code) {
              <span
                class="pill sm m-r-5 m-b-5"
                [class.gray]="langStatus[lang.code] === 'idle' || langStatus[lang.code] === 'translating'"
                [class.green]="langStatus[lang.code] === 'done'"
                [class.red]="langStatus[lang.code] === 'error'"
              >
                @if (langStatus[lang.code] === "translating") {
                  <fa-icon icon="spinner" animation="spin"></fa-icon>
                } @else if (langStatus[lang.code] === "done") {
                  <fa-icon icon="check"></fa-icon>
                } @else if (langStatus[lang.code] === "error") {
                  <fa-icon icon="exclamation-triangle"></fa-icon>
                }
                {{ lang.code }}
              </span>
            }
          </div>
        </div>
      } @else {
        @if (missingTranslations) {
          <div class="d-flex flex-wrap m-t-10 m-b-5">
            @for (lang of otherLanguages; track lang.code) {
              <span class="pill sm m-r-5 m-b-5" [class.gray]="langStatus[lang.code] === 'idle'" [class.green]="langStatus[lang.code] === 'done'" [class.red]="langStatus[lang.code] === 'error'">
                @if (langStatus[lang.code] === "done") {
                  <fa-icon icon="check"></fa-icon>
                } @else if (langStatus[lang.code] === "error") {
                  <fa-icon icon="exclamation-triangle"></fa-icon>
                }
                {{ lang.code }}
              </span>
            }
          </div>
        }

        <button type="button" class="btn btn-blank btn-sm m-t-10" (click)="showAllLanguages = !showAllLanguages">
          {{ showAllLanguages ? "Hide other languages" : "Show all languages (" + otherLanguages.length + ")" }}
        </button>

        @if (showAllLanguages) {
          @for (lang of otherLanguages; track lang.code) {
            <div class="form-group m-t-15">
              <label class="control-label">
                {{ lang.translated_name || lang.name }} ({{ lang.code }})
                @if (missingByLang[lang.code]) {
                  <span class="t-12 text-muted">— missing</span>
                }
              </label>
              @if (quillReady) {
                <fui-quill-editor
                  [content]="translations[lang.code] || ''"
                  (contentChange)="onTranslationChange(lang.code, $event)"
                  [placeholder]="'Write ' + (lang.translated_name || lang.name) + ' content...'"
                  height="220px"
                ></fui-quill-editor>
              }
            </div>
          }
        }
      }
    }
  </div>
</div>
<div class="modal-footer">
  <button class="btn btn-blank" [disabled]="saving || translating" (click)="activeModal.dismiss()">Cancel</button>
  <button class="btn btn-primary" [disabled]="saving || translating" (click)="save()">
    @if (saving) {
      <fa-icon icon="spinner" animation="spin" class="m-r-5"></fa-icon>
    }
    {{ isNew ? "Create" : "Save" }}
  </button>
</div>
